import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Paper,
  Chip,
  Fade,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Psychology,
  Rocket,
  TrendingUp,
  Star,
  CheckCircle,
  Transform,
  EmojiEvents,
  Lightbulb,
  Build,
  AutoAwesome,
  Self_improvement,
  Favorite
} from '@mui/icons-material';

const TransformationLandingPage = () => {
  const navigate = useNavigate();
  const { session } = useAuth();

  // Redirect authenticated users to conversation
  useEffect(() => {
    if (session?.user) {
      navigate('/conversation');
    }
  }, [session, navigate]);

  const handleGetStarted = () => {
    navigate('/login?mode=signup');
  };

  const handleLogin = () => {
    navigate('/login');
  };

  const features = [
    {
      icon: <Transform sx={{ fontSize: 40, color: '#E91E63' }} />,
      title: "Complete Life Redesign",
      description: "Transform every aspect of your life with precision. Break free from limitations and step into the confident, empowered woman you're meant to be.",
      gradient: "linear-gradient(135deg, #c2185b, #e91e63, #f06292)"
    },
    {
      icon: <EmojiEvents sx={{ fontSize: 40, color: '#9C27B0' }} />,
      title: "Confidence & Empowerment",
      description: "Develop unshakeable self-worth and inner strength. Master the mindset of successful women who command respect and achieve their dreams.",
      gradient: "linear-gradient(135deg, #7b1fa2, #9c27b0, #ba68c8)"
    },
    {
      icon: <AutoAwesome sx={{ fontSize: 40, color: '#FF5722' }} />,
      title: "Radiant Success",
      description: "Unlock your full potential and create the extraordinary life you deserve. Accelerate your goals with proven strategies for lasting transformation.",
      gradient: "linear-gradient(135deg, #d84315, #ff5722, #ff7043)"
    }
  ];

  const transformationBenefits = [
    "Transform from ordinary to absolutely extraordinary",
    "Build radiant confidence that lights up every room",
    "Achieve dreams others said were impossible",
    "Create deep, meaningful relationships",
    "Design a life filled with purpose and passion",
    "Become the inspiring woman others look up to"
  ];

  const transformationTestimonials = [
    {
      name: "Sarah M.",
      role: "Empowered Entrepreneur",
      text: "I went from feeling invisible to becoming the CEO of my own company. This program didn't just change my mindset - it completely transformed who I am. I'm finally living as my authentic self.",
      avatar: "✨",
      result: "Built 6-figure business from scratch"
    },
    {
      name: "Jessica L.",
      role: "Confident Leader",
      result: "Promoted to Executive Director",
      text: "The transformation was incredible. From someone who doubted every decision to a confident leader inspiring 100+ people. Every relationship in my life improved dramatically.",
      avatar: "👑"
    },
    {
      name: "Maria R.",
      role: "Life Champion",
      result: "Complete life transformation in 6 months",
      text: "I thought I was destined for mediocrity. This program helped me discover my true power. Now I'm living the life I only dreamed about before.",
      avatar: "🦋"
    }
  ];

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a0e27 0%, #1a1a2e 25%, #16213e 50%, #0f3460 75%, #533483 100%)',
      position: 'relative'
    }}>
      {/* Powerful Animated Background */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        opacity: 0.15,
        background: `
          radial-gradient(circle at 25% 25%, #E91E63 0%, transparent 50%),
          radial-gradient(circle at 75% 75%, #9C27B0 0%, transparent 50%),
          radial-gradient(circle at 50% 50%, #FF5722 0%, transparent 70%),
          linear-gradient(45deg, transparent 40%, rgba(233, 30, 99, 0.1) 50%, transparent 60%)
        `,
        animation: 'backgroundPulse 8s ease-in-out infinite alternate'
      }} />

      {/* CSS Animation */}
      <style>
        {`
          @keyframes backgroundPulse {
            0% { opacity: 0.1; }
            100% { opacity: 0.2; }
          }
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
          }
          @keyframes glow {
            0%, 100% { box-shadow: 0 0 20px rgba(233, 30, 99, 0.3); }
            50% { box-shadow: 0 0 40px rgba(233, 30, 99, 0.6), 0 0 60px rgba(233, 30, 99, 0.4); }
          }
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
          }
        `}
      </style>

      {/* Premium Header */}
      <Box sx={{ 
        position: 'relative', 
        zIndex: 10,
        py: { xs: 1, md: 1.5 },
        px: { xs: 2, md: 4 },
        background: 'rgba(0,0,0,0.3)',
        backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(255,255,255,0.1)'
      }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          maxWidth: '1400px',
          margin: '0 auto'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              width: 52,
              height: 52,
              borderRadius: '50%',
              background: 'linear-gradient(45deg, #E91E63, #9C27B0)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 25px rgba(233, 30, 99, 0.4)',
              animation: 'glow 3s ease-in-out infinite'
            }}>
              <img src="/sanity_logo.png" alt="Sanity Logo" style={{ 
                width: 36, 
                height: 36,
                filter: 'brightness(1.2)'
              }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ 
                fontWeight: 'bold', 
                color: 'white',
                fontSize: { xs: '1.4rem', md: '1.8rem' },
                textShadow: '0 2px 10px rgba(0,0,0,0.5)'
              }}>
                Sanity Reps
              </Typography>
              <Typography variant="caption" sx={{ 
                color: '#E91E63',
                fontWeight: 'bold',
                fontSize: '0.75rem',
                letterSpacing: '1px'
              }}>
                FEMALE EMPOWERMENT
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: { xs: 1, md: 2 }, alignItems: 'center' }}>
            <Button 
              variant="outlined" 
              onClick={handleLogin}
              sx={{ 
                color: 'white', 
                borderColor: 'rgba(255,255,255,0.3)',
                px: { xs: 2, md: 3 },
                py: 1,
                fontSize: { xs: '0.8rem', md: '0.9rem' },
                fontWeight: 'bold',
                borderRadius: 3,
                backdropFilter: 'blur(10px)',
                '&:hover': { 
                  borderColor: '#E91E63',
                  bgcolor: 'rgba(233, 30, 99, 0.1)',
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              Login
            </Button>
            <Button 
              variant="contained" 
              onClick={handleGetStarted}
              sx={{ 
                background: 'linear-gradient(45deg, #E91E63, #9C27B0)',
                color: 'white',
                fontWeight: 'bold',
                px: { xs: 2.5, md: 4 },
                py: 1.2,
                fontSize: { xs: '0.85rem', md: '1rem' },
                borderRadius: 3,
                boxShadow: '0 8px 25px rgba(233, 30, 99, 0.4)',
                '&:hover': { 
                  background: 'linear-gradient(45deg, #C2185B, #7B1FA2)',
                  transform: 'translateY(-3px)',
                  boxShadow: '0 12px 35px rgba(233, 30, 99, 0.6)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              ✨ TRANSFORM NOW
            </Button>
          </Box>
        </Box>
      </Box>

      {/* HERO: Complete Life Transformation */}
      <Container maxWidth="xl" sx={{ 
        position: 'relative', 
        zIndex: 10, 
        pt: { xs: 2, md: 2 },
        pb: { xs: 3, md: 2 },
        minHeight: { md: 'calc(100vh - 80px)' },
        maxHeight: { md: 'calc(100vh - 80px)' },
        display: { md: 'flex' },
        alignItems: { md: 'center' }
      }}>
        <Grid container spacing={{ xs: 3, md: 4 }} alignItems="center" sx={{ height: '100%' }}>
          {/* LEFT: Compelling Copy */}
          <Grid item xs={12} md={8}>
            <Fade in timeout={800}>
              <Box sx={{ pr: { md: 3 } }}>
                {/* Attention-Grabbing Badge */}
                <Box sx={{ mb: 2 }}>
                  <Chip 
                    label="✨ COMPLETE FEMALE TRANSFORMATION SYSTEM" 
                    sx={{ 
                      background: 'linear-gradient(45deg, rgba(233, 30, 99, 0.2), rgba(156, 39, 176, 0.2))',
                      color: '#E91E63',
                      fontWeight: 'bold',
                      fontSize: { xs: '0.75rem', md: '0.85rem' },
                      border: '1px solid rgba(233, 30, 99, 0.5)',
                      backdropFilter: 'blur(10px)',
                      px: 2,
                      py: 0.5,
                      animation: 'float 3s ease-in-out infinite'
                    }} 
                  />
                </Box>

                {/* Power Headline */}
                <Typography variant="h1" sx={{ 
                  fontSize: { xs: '2.2rem', sm: '2.8rem', md: '3.2rem', lg: '3.8rem' },
                  fontWeight: 900,
                  color: 'white',
                  mb: { xs: 2, md: 1.5 },
                  lineHeight: 0.95,
                  textShadow: '0 4px 20px rgba(0,0,0,0.7)',
                  letterSpacing: '-0.02em'
                }}>
                  BECOME THE 
                  <br />
                  <Box component="span" sx={{ 
                    background: 'linear-gradient(45deg, #E91E63, #9C27B0, #FF5722)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}>
                    RADIANT WOMAN
                  </Box>
                  <br />
                  <Box component="span" sx={{ color: '#9C27B0' }}>
                    YOU'RE MEANT TO BE
                  </Box>
                </Typography>
                
                {/* Power Subheadline */}
                <Typography variant="h4" sx={{ 
                  fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.4rem' },
                  color: 'rgba(255,255,255,0.95)',
                  mb: { xs: 2.5, md: 3 },
                  fontWeight: 400,
                  lineHeight: 1.3,
                  maxWidth: '600px'
                }}>
                  Step into your power and create the extraordinary life you deserve. Transform every aspect of your being and become the <strong style={{color: '#E91E63'}}>confident, empowered woman</strong> you've always dreamed of being.
                </Typography>

                {/* Rapid Benefits */}
                <Box sx={{ mb: { xs: 2.5, md: 3 } }}>
                  <Grid container spacing={2}>
                    {[
                      { icon: '✨', text: 'Complete transformation in 90 days', color: '#E91E63' },
                      { icon: '👑', text: 'Radiant confidence & self-worth', color: '#9C27B0' },
                      { icon: '🦋', text: 'Become the woman you admire', color: '#FF5722' }
                    ].map((benefit, index) => (
                      <Grid item xs={12} sm={4} key={index}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1.5,
                          p: { xs: 1.5, md: 2 },
                          borderRadius: 3,
                          background: 'rgba(255,255,255,0.05)',
                          border: `1px solid rgba(${benefit.color === '#E91E63' ? '233, 30, 99' : benefit.color === '#9C27B0' ? '156, 39, 176' : '255, 87, 34'}, 0.3)`,
                          backdropFilter: 'blur(10px)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-3px)',
                            background: 'rgba(255,255,255,0.1)'
                          }
                        }}>
                          <Typography sx={{ fontSize: { xs: '1.2rem', md: '1.5rem' } }}>{benefit.icon}</Typography>
                          <Typography variant="body2" sx={{ 
                            color: 'white', 
                            fontWeight: 600,
                            fontSize: { xs: '0.8rem', md: '0.9rem' }
                          }}>
                            {benefit.text}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
                
                {/* CTA Buttons */}
                <Box sx={{ 
                  display: 'flex', 
                  gap: { xs: 2, md: 3 }, 
                  flexDirection: { xs: 'column', sm: 'row' }, 
                  alignItems: { xs: 'stretch', sm: 'center' },
                  mb: { xs: 3, md: 0 }
                }}>
                  <Button 
                    variant="contained" 
                    size="large"
                    onClick={handleGetStarted}
                    sx={{ 
                      background: 'linear-gradient(45deg, #E91E63, #9C27B0)',
                      color: 'white',
                      px: { xs: 3, md: 4 },
                      py: { xs: 1.5, md: 2 },
                      fontSize: { xs: '1rem', md: '1.3rem' },
                      fontWeight: 'bold',
                      borderRadius: 4,
                      boxShadow: '0 12px 35px rgba(233, 30, 99, 0.4)',
                      minWidth: { sm: '280px' },
                      '&:hover': { 
                        background: 'linear-gradient(45deg, #C2185B, #7B1FA2)',
                        transform: 'translateY(-4px)',
                        boxShadow: '0 16px 45px rgba(233, 30, 99, 0.6)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    ✨ START TRANSFORMATION
                  </Button>
                  
                  <Button 
                    variant="outlined" 
                    size="large"
                    onClick={() => document.getElementById('testimonials').scrollIntoView({ behavior: 'smooth' })}
                    sx={{ 
                      color: 'white',
                      borderColor: 'rgba(255,255,255,0.5)',
                      px: 3,
                      py: { xs: 1.5, md: 2 },
                      fontSize: { xs: '0.9rem', md: '1rem' },
                      borderRadius: 4,
                      backdropFilter: 'blur(10px)',
                      '&:hover': { 
                        bgcolor: 'rgba(255,255,255,0.1)',
                        borderColor: 'white',
                        transform: 'translateY(-2px)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    🦋 See Transformations
                  </Button>
                </Box>
              </Box>
            </Fade>
          </Grid>
          
          {/* RIGHT: Powerful Transformation Visual */}
          <Grid item xs={12} md={4}>
            <Fade in timeout={1200}>
              <Box sx={{ 
                position: 'relative',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: { xs: 'auto', md: '420px' },
                mt: { xs: 2, md: 0 }
              }}>
                {/* Main Transformation Image */}
                <Box sx={{
                  position: 'relative',
                  borderRadius: 4,
                  overflow: 'hidden',
                  boxShadow: '0 25px 60px rgba(0,0,0,0.4)',
                  border: '3px solid rgba(233, 30, 99, 0.5)',
                  background: 'linear-gradient(145deg, rgba(255,255,255,0.1), rgba(255,255,255,0.05))',
                  backdropFilter: 'blur(20px)',
                  transition: 'all 0.4s ease',
                  maxWidth: { xs: '100%', md: '400px' },
                  width: '100%',
                  animation: 'float 6s ease-in-out infinite',
                  '&:hover': {
                    transform: 'scale(1.05) translateY(-10px)',
                    boxShadow: '0 35px 80px rgba(0,0,0,0.5)',
                    border: '3px solid rgba(233, 30, 99, 0.8)'
                  }
                }}>
                  <img 
                    src="/before_after_girl.png" 
                    alt="Female Life Transformation - Before vs After Radiant Woman" 
                    style={{
                      width: '100%',
                      height: 'auto',
                      display: 'block'
                    }}
                  />
                  
                  {/* Premium Before/After Labels */}
                  <Box sx={{
                    position: 'absolute',
                    top: 12,
                    left: 12,
                    background: 'linear-gradient(135deg, #dc3545, #c82333)',
                    color: 'white',
                    px: { xs: 1.5, md: 2 },
                    py: { xs: 0.8, md: 1 },
                    borderRadius: 4,
                    fontWeight: 'bold',
                    fontSize: { xs: '0.7rem', md: '0.8rem' },
                    backdropFilter: 'blur(15px)',
                    border: '2px solid rgba(255,255,255,0.4)',
                    boxShadow: '0 6px 20px rgba(220, 53, 69, 0.5)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    zIndex: 3,
                    animation: 'pulse 3s ease-in-out infinite',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: '0 8px 25px rgba(220, 53, 69, 0.7)'
                    },
                    transition: 'all 0.3s ease'
                  }}>
                    😔 Before: Unsure
                  </Box>
                  
                  <Box sx={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    background: 'linear-gradient(135deg, #28a745, #20c997)',
                    color: 'white',
                    px: { xs: 1.5, md: 2 },
                    py: { xs: 0.8, md: 1 },
                    borderRadius: 4,
                    fontWeight: 'bold',
                    fontSize: { xs: '0.7rem', md: '0.8rem' },
                    backdropFilter: 'blur(15px)',
                    border: '2px solid rgba(255,255,255,0.4)',
                    boxShadow: '0 6px 20px rgba(40, 167, 69, 0.5)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    zIndex: 3,
                    animation: 'glow 3s ease-in-out infinite',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: '0 8px 25px rgba(40, 167, 69, 0.7)'
                    },
                    transition: 'all 0.3s ease'
                  }}>
                    ✨ After: Radiant
                  </Box>
                  
                  {/* Results Overlay */}
                  <Box sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: 'linear-gradient(0deg, rgba(0,0,0,0.9) 0%, transparent 100%)',
                    color: 'white',
                    p: 3,
                    textAlign: 'center'
                  }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: '#E91E63' }}>
                      ✨ COMPLETE TRANSFORMATION
                    </Typography>
                    <Grid container spacing={2}>
                      {[
                        { number: "∞", label: "Potential", color: '#E91E63' },
                        { number: "100%", label: "Confidence", color: '#9C27B0' },
                        { number: "10x", label: "Radiance", color: '#FF5722' }
                      ].map((stat, index) => (
                        <Grid item xs={4} key={index}>
                          <Typography variant="h5" sx={{ 
                            color: stat.color, 
                            fontWeight: 'bold',
                            textShadow: `0 2px 10px ${stat.color}50`
                          }}>
                            {stat.number}
                          </Typography>
                          <Typography variant="caption" sx={{ 
                            color: 'rgba(255,255,255,0.9)',
                            fontSize: '0.7rem',
                            fontWeight: 600
                          }}>
                            {stat.label}
                          </Typography>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </Box>

                {/* Floating Achievement Indicators */}
                <Box sx={{
                  position: 'absolute',
                  top: -20,
                  left: -20,
                  background: 'linear-gradient(45deg, #E91E63, #9C27B0)',
                  color: 'white',
                  px: 2,
                  py: 1,
                  borderRadius: 3,
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  boxShadow: '0 8px 20px rgba(233, 30, 99, 0.4)',
                  animation: 'float 4s ease-in-out infinite'
                }}>
                  ⭐ 5/5 Transformation
                </Box>
                
                <Box sx={{
                  position: 'absolute',
                  bottom: -20,
                  right: -20,
                  background: 'linear-gradient(45deg, #9C27B0, #FF5722)',
                  color: 'white',
                  px: 2,
                  py: 1,
                  borderRadius: 3,
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  boxShadow: '0 8px 20px rgba(156, 39, 176, 0.4)',
                  animation: 'float 4s ease-in-out infinite 2s'
                }}>
                  👑 12,000+ Queens
                </Box>
              </Box>
            </Fade>
          </Grid>
        </Grid>
      </Container>

      {/* SOCIAL PROOF: Elite credibility */}
      <Box sx={{ 
        py: { xs: 4, md: 6 }, 
        background: 'rgba(0,0,0,0.2)',
        backdropFilter: 'blur(20px)',
        borderTop: '1px solid rgba(255,255,255,0.1)',
        borderBottom: '1px solid rgba(255,255,255,0.1)'
      }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h5" sx={{ 
                color: 'white',
                fontWeight: 'bold',
                mb: 2,
                textAlign: { xs: 'center', md: 'left' }
              }}>
                ✨ Trusted by Empowered Women & Confident Leaders
              </Typography>
              <Typography variant="body1" sx={{ 
                color: 'rgba(255,255,255,0.8)',
                textAlign: { xs: 'center', md: 'left' }
              }}>
                Join 12,000+ radiant women who've transformed their lives completely
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: { xs: 'center', md: 'flex-end' },
                gap: 3
              }}>
                {[
                  { number: "5.0", label: "★★★★★", subtext: "4,156 Reviews" },
                  { number: "12K+", label: "Queens Created", subtext: "Transformed" },
                  { number: "90", label: "Day Results", subtext: "Guaranteed" }
                ].map((stat, index) => (
                  <Box key={index} sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" sx={{ 
                      color: '#E91E63', 
                      fontWeight: 'bold',
                      fontSize: '1.5rem'
                    }}>
                      {stat.number}
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      color: '#9C27B0',
                      fontWeight: 'bold',
                      fontSize: '0.8rem'
                    }}>
                      {stat.label}
                    </Typography>
                    <Typography variant="caption" sx={{ 
                      color: 'rgba(255,255,255,0.7)',
                      fontSize: '0.7rem'
                    }}>
                      {stat.subtext}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* FEATURES: Transformation Arsenal */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #1A0E2E 0%, #2D1B4E 25%, #3E2A5A 50%, #4A2C66 75%, #5D3973 100%)',
        py: { xs: 8, md: 12 }, 
        position: 'relative',
        borderTop: '1px solid rgba(255,255,255,0.1)'
      }}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 10 }}>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="h2" sx={{ 
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              fontWeight: 'bold',
              color: 'white',
              mb: 3,
              textShadow: '0 4px 20px rgba(0,0,0,0.5)'
            }}>
              Your <Box component="span" sx={{ color: '#E1BEE7' }}>Transformation Journey</Box>
            </Typography>
            <Typography variant="h5" sx={{ 
              color: 'rgba(255,255,255,0.9)',
              maxWidth: '600px',
              margin: '0 auto',
              fontWeight: 400,
              textShadow: '0 2px 10px rgba(0,0,0,0.3)'
            }}>
              Revolutionary methods designed specifically for empowered women ready to shine
            </Typography>
          </Box>
          
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Fade in timeout={1000 + index * 200}>
                  <Card sx={{ 
                    height: '100%',
                    background: `linear-gradient(135deg, ${feature.gradient})`,
                    color: 'white',
                    borderRadius: 4,
                    border: '1px solid rgba(225, 190, 231, 0.3)',
                    transition: 'all 0.4s ease',
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.4)',
                    '&:hover': { 
                      transform: 'translateY(-15px) scale(1.02)',
                      boxShadow: '0 25px 50px rgba(225, 190, 231, 0.3)',
                      border: '1px solid rgba(225, 190, 231, 0.6)',
                      '& .feature-icon': {
                        transform: 'scale(1.2) rotate(5deg)'
                      }
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'linear-gradient(45deg, rgba(0,0,0,0.4), rgba(0,0,0,0.2))',
                      opacity: 1,
                      transition: 'opacity 0.3s ease',
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'linear-gradient(45deg, rgba(225, 190, 231, 0.1), transparent)',
                      opacity: 0,
                      transition: 'opacity 0.3s ease',
                    },
                    '&:hover::after': {
                      opacity: 1
                    }
                  }}>
                    <CardContent sx={{ p: 4, textAlign: 'center', position: 'relative', zIndex: 2 }}>
                      <Box className="feature-icon" sx={{ 
                        mb: 3, 
                        transition: 'all 0.3s ease',
                        display: 'inline-block'
                      }}>
                        {React.cloneElement(feature.icon, { sx: { fontSize: 50, color: '#E1BEE7', filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.5))' } })}
                      </Box>
                      <Typography variant="h5" sx={{ 
                        mb: 3, 
                        fontWeight: 'bold',
                        color: 'white',
                        textShadow: '0 2px 10px rgba(0,0,0,0.8), 0 4px 20px rgba(0,0,0,0.6)'
                      }}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body1" sx={{ 
                        color: 'rgba(255,255,255,0.95)', 
                        lineHeight: 1.6,
                        fontSize: '1rem',
                        textShadow: '0 2px 8px rgba(0,0,0,0.8), 0 4px 16px rgba(0,0,0,0.6)',
                        fontWeight: 400
                      }}>
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* TESTIMONIALS: Radiant transformations */}
      <Box id="testimonials" sx={{ 
        py: { xs: 8, md: 12 }, 
        background: 'linear-gradient(135deg, #0D4F1C 0%, #1B5E20 25%, #2E7D32 50%, #388E3C 75%, #43A047 100%)',
        position: 'relative',
        borderTop: '1px solid rgba(67, 160, 71, 0.3)'
      }}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 10 }}>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="h2" sx={{ 
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              fontWeight: 'bold',
              color: 'white',
              mb: 3,
              textShadow: '0 4px 20px rgba(0,0,0,0.5)'
            }}>
              <Box component="span" sx={{ color: '#A5D6A7' }}>Radiant Women,</Box> Epic Results
            </Typography>
            <Typography variant="h5" sx={{ 
              color: 'rgba(255,255,255,0.8)',
              maxWidth: '600px',
              margin: '0 auto',
              fontWeight: 300
            }}>
              See how ordinary women became extraordinary leaders in 90 days
            </Typography>
          </Box>
          
          <Grid container spacing={4}>
            {transformationTestimonials.map((testimonial, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Fade in timeout={1000 + index * 300}>
                  <Card sx={{ 
                    height: '100%',
                    background: 'linear-gradient(135deg, rgba(67, 160, 71, 0.2), rgba(56, 142, 60, 0.3))',
                    backdropFilter: 'blur(20px)',
                    border: '2px solid rgba(165, 214, 167, 0.2)',
                    borderRadius: 4,
                    transition: 'all 0.4s ease',
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: '0 8px 25px rgba(67, 160, 71, 0.3)',
                    '&:hover': { 
                      transform: 'translateY(-10px)',
                      background: 'linear-gradient(135deg, rgba(67, 160, 71, 0.3), rgba(56, 142, 60, 0.4))',
                      border: '2px solid rgba(165, 214, 167, 0.5)',
                      boxShadow: '0 20px 40px rgba(67, 160, 71, 0.4)'
                    }
                  }}>
                    <CardContent sx={{ p: 4, position: 'relative', zIndex: 2 }}>
                      <Box sx={{ textAlign: 'center', mb: 3 }}>
                        <Typography sx={{ 
                          fontSize: '4rem', 
                          mb: 2,
                          textShadow: '0 4px 15px rgba(0,0,0,0.3)'
                        }}>
                          {testimonial.avatar}
                        </Typography>
                        <Typography variant="h6" sx={{ 
                          color: 'white', 
                          fontWeight: 'bold',
                          fontSize: '1.2rem'
                        }}>
                          {testimonial.name}
                        </Typography>
                        <Typography variant="body2" sx={{ 
                          color: '#A5D6A7', 
                          fontWeight: 'bold',
                          fontSize: '0.9rem'
                        }}>
                          {testimonial.role}
                        </Typography>
                        {testimonial.result && (
                          <Box sx={{
                            mt: 1,
                            p: 1,
                            borderRadius: 2,
                            background: 'linear-gradient(45deg, #2E7D32, #43A047)',
                            border: '1px solid rgba(165, 214, 167, 0.3)'
                          }}>
                            <Typography variant="caption" sx={{ 
                              color: 'white',
                              fontWeight: 'bold',
                              fontSize: '0.75rem'
                            }}>
                              👑 {testimonial.result}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                      <Box sx={{
                        background: 'rgba(0,0,0,0.3)',
                        p: 3,
                        borderRadius: 3,
                        border: '1px solid rgba(165, 214, 167, 0.2)'
                      }}>
                        <Typography variant="body1" sx={{ 
                          color: 'rgba(255,255,255,0.95)',
                          fontStyle: 'italic',
                          lineHeight: 1.6,
                          textAlign: 'center',
                          fontSize: '1rem'
                        }}>
                          "{testimonial.text}"
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* FINAL CTA: Radiant transformation */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #B71C1C 0%, #D32F2F 25%, #F44336 50%, #FF5722 75%, #FF7043 100%)',
        py: { xs: 8, md: 12 }, 
        position: 'relative',
        borderTop: '1px solid rgba(255, 112, 67, 0.3)'
      }}>
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 10 }}>
          <Paper sx={{ 
            background: 'linear-gradient(135deg, #D32F2F, #FF5722, #FF7043)',
            color: 'white',
            p: { xs: 6, md: 8 },
            textAlign: 'center',
            borderRadius: 6,
            boxShadow: '0 25px 60px rgba(0,0,0,0.4)',
            border: '2px solid rgba(255, 112, 67, 0.4)',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -50,
              left: -50,
              right: -50,
              bottom: -50,
              background: 'radial-gradient(circle, rgba(255, 112, 67, 0.2) 0%, transparent 70%)',
              animation: 'glow 4s ease-in-out infinite'
            }
          }}>
            <Box sx={{ position: 'relative', zIndex: 2 }}>
              <Box sx={{ fontSize: '4rem', mb: 3, animation: 'pulse 3s ease-in-out infinite' }}>✨</Box>
              <Typography variant="h2" sx={{ 
                mb: 4, 
                fontWeight: 'bold',
                fontSize: { xs: '2rem', md: '3rem' },
                textShadow: '0 4px 20px rgba(0,0,0,0.3)'
              }}>
                Ready to Become <Box component="span" sx={{ color: '#FFCCBC' }}>Absolutely Radiant?</Box>
              </Typography>
              <Typography variant="h5" sx={{ 
                mb: 6, 
                opacity: 0.95,
                maxWidth: '500px',
                margin: '0 auto 2rem auto',
                fontWeight: 300
              }}>
                Join the sisterhood of empowered women who've unlocked their true potential and created extraordinary lives. 
                <strong style={{color: '#FFCCBC'}}> Your radiant transformation starts now.</strong>
              </Typography>
              
              <Box sx={{ mb: 4 }}>
                <Typography variant="body1" sx={{ 
                  color: '#FFCCBC',
                  fontWeight: 'bold',
                  fontSize: '1.1rem',
                  mb: 2
                }}>
                  ✨ Complete Life Transformation in 90 Days or Get Your Money Back
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mb: 3, flexWrap: 'wrap' }}>
                  {[
                    "✅ Zero Risk - 100% Guarantee",
                    "⚡ Instant Access to Everything",
                    "👑 Join Elite Sisterhood"
                  ].map((benefit, index) => (
                    <Typography key={index} variant="body2" sx={{ 
                      color: 'rgba(255,255,255,0.9)',
                      fontWeight: 600,
                      fontSize: '0.9rem'
                    }}>
                      {benefit}
                    </Typography>
                  ))}
                </Box>
              </Box>

              <Button 
                variant="contained" 
                size="large"
                onClick={handleGetStarted}
                sx={{ 
                  bgcolor: 'white',
                  color: '#D32F2F',
                  px: 6,
                  py: 3,
                  fontSize: '1.4rem',
                  fontWeight: 'bold',
                  borderRadius: 4,
                  minWidth: '320px',
                  boxShadow: '0 15px 35px rgba(0,0,0,0.3)',
                  '&:hover': { 
                    bgcolor: '#f8f9fa',
                    transform: 'translateY(-5px) scale(1.05)',
                    boxShadow: '0 20px 45px rgba(0,0,0,0.4)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                ✨ START MY RADIANT TRANSFORMATION
              </Button>
              
              <Typography variant="caption" sx={{ 
                display: 'block',
                mt: 3,
                color: 'rgba(255,255,255,0.7)',
                fontSize: '0.8rem'
              }}>
                ⏰ Limited Time: Save 70% off regular price
              </Typography>
            </Box>
          </Paper>
        </Container>
      </Box>
    </Box>
  );
};

export default TransformationLandingPage;
